import { formatMonthDay, monthName } from '@trips/shared/format';
import { type BuyDeal, buyCpp, buyDeals } from '@trips/shared/news';
import type { Brief } from '@trips/shared/schema';
import { useMemo, useState } from 'react';
import { selectPrefs, selectToday } from '../../data/selectors';
import { useStore } from '../../data/store';
import { Inline, Markdown } from '../../lib/markdown';
import { Link } from '../../lib/router';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { Page } from '../../ui/Page';
import s from './News.module.css';

/**
 * News: the points & miles briefs Claude writes every morning, week and month, newest first,
 * with the best points-buying deals from all of them pulled to the top. They arrive through
 * sync the moment Claude saves one.
 */

type Kind = Brief['kind'];
const KIND: Record<Kind, { label: string; emoji: string }> = {
  daily: { label: 'Daily', emoji: '☀️' },
  weekly: { label: 'Weekly', emoji: '🗞️' },
  monthly: { label: 'Monthly', emoji: '📅' },
};

function useBriefs(): Brief[] {
  const table = useStore((st) => st.tables.briefs);
  return useMemo(
    () =>
      [...table.values()]
        .filter((b) => !b.deleted)
        .sort((a, b) => b.date.localeCompare(a.date) || (b.writtenAt ?? 0) - (a.writtenAt ?? 0)),
    [table],
  );
}

function dayLabel(date: string, today: string, prefs: { dateFormat: 'us' | 'intl' }) {
  if (date === today) return 'Today';
  const y = new Date(Date.parse(`${today}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  if (date === y) return 'Yesterday';
  return `${formatMonthDay(date, prefs)}${date.slice(0, 4) !== today.slice(0, 4) ? `, ${date.slice(0, 4)}` : ''}`;
}

const pts = (n: number) =>
  n >= 1000 && n % 1000 === 0
    ? `${(n / 1000).toLocaleString('en-US')}k`
    : n.toLocaleString('en-US');
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/** "Buy 1k to 160k points · $720 for the most" (cost at the sale price). */
function limitsLine(min: number | null, max: number | null, cpp: number | null) {
  const range =
    min != null && max != null
      ? `Buy ${pts(min)} to ${pts(max)} points`
      : max != null
        ? `Buy up to ${pts(max)} points`
        : `Buy at least ${pts(min!)} points`;
  const cost = max != null && cpp != null ? ` · ${usd((max * cpp) / 100)} for the most` : '';
  return range + cost;
}

/** "May 2026" from 2026-05, or the year alone. */
const monthYear = (when: string) =>
  when.length >= 7 ? `${monthName(Number(when.slice(5, 7)))} ${when.slice(0, 4)}` : when;

/** The deal against the lowest price its currency has ever sold for. */
function bestLine(d: BuyDeal): { text: string; low: boolean } | null {
  const b = d.bestEver;
  if (!b) return null;
  const when = b.when ? ` · ${monthYear(b.when)}` : '';
  const lowest = d.costCpp <= b.cpp;
  // No published record for this currency: only my briefs to go on.
  if (b.from === 'briefs' && b.beat == null)
    return lowest
      ? { text: 'Lowest price in your briefs so far', low: true }
      : { text: `Lowest in your briefs ${b.cpp}¢${when}`, low: false };
  // A sale in my briefs went below the published record.
  if (b.from === 'briefs')
    return lowest
      ? { text: `New record low (was ${b.beat}¢)`, low: true }
      : { text: `Best ever ${b.cpp}¢${when}`, low: false };
  if (lowest)
    return {
      text:
        b.when === d.seenOn.slice(0, 7) ? 'Lowest price ever' : `Ties the best price ever${when}`,
      low: true,
    };
  return { text: `Best ever ${b.cpp}¢${when}${b.promo ? `, ${b.promo}` : ''}`, low: false };
}

function External({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a className={s.ext} href={href} target="_blank" rel="noreferrer noopener">
      {children}
      <Icon name="arrow-square-out" size={14} />
    </a>
  );
}

function DealCard({
  d,
  today,
  prefs,
}: {
  d: BuyDeal;
  today: string;
  prefs: { dateFormat: 'us' | 'intl' };
}) {
  const best = bestLine(d);
  const body = (
    <>
      <span className={s.dealTop}>
        <span className={s.dealName}>
          {d.emoji ? `${d.emoji} ` : ''}
          {d.program?.name ?? d.title}
        </span>
        {d.ratio != null && (
          <span className={s.ratio} data-good={d.ratio >= 1 || undefined}>
            {d.ratio.toFixed(2)}× value
          </span>
        )}
      </span>
      <span className={`${s.dealPrice} num`}>
        {d.costCpp}¢<small> a point</small>
      </span>
      <span className={`${s.dealMeta} num`}>
        {d.valueCpp != null ? `Worth about ${d.valueCpp}¢` : d.title}
        {d.endsOn ? ` · ends ${dayLabel(d.endsOn, today, prefs)}` : ''}
      </span>
      {best && (
        <span className={`${s.best} num`} data-low={best.low || undefined}>
          {best.low && <Icon name="check" size={13} />}
          {best.text}
        </span>
      )}
      {(d.minPoints != null || d.maxPoints != null) && (
        <span className={`${s.limits} num`}>{limitsLine(d.minPoints, d.maxPoints, d.costCpp)}</span>
      )}
      {d.program && <span className={s.dealSub}>{d.title}</span>}
    </>
  );
  return d.url ? (
    <a className={s.deal} href={d.url} target="_blank" rel="noreferrer noopener">
      {body}
    </a>
  ) : (
    <div className={s.deal}>{body}</div>
  );
}

export function NewsScreen() {
  const briefs = useBriefs();
  const today = useStore(selectToday);
  const prefs = useStore(selectPrefs);
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const deals = useMemo(() => buyDeals(briefs, today), [briefs, today]);
  const shown = briefs.filter((b) => kind === 'all' || b.kind === kind);

  if (!briefs.length)
    return (
      <Page title="News">
        <EmptyState icon="newspaper" title="Your points briefs will show here">
          <p>Claude saves each daily, weekly and monthly brief here as soon as it's written.</p>
        </EmptyState>
      </Page>
    );

  return (
    <Page title="News" wide>
      {deals.length > 0 && (
        <section className={s.block}>
          <h2 className={s.blockTitle}>Best points to buy</h2>
          <p className={`${s.blockNote} t-footnote`}>
            Sales from your briefs that are still on, best value for the money first.
          </p>
          <div className={s.deals}>
            {deals.slice(0, 12).map((d) => (
              <DealCard key={d.key} d={d} today={today} prefs={prefs} />
            ))}
          </div>
        </section>
      )}

      <fieldset className={s.chips}>
        <legend className="sr-only">Which briefs</legend>
        {(['all', 'daily', 'weekly', 'monthly'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={s.chip}
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
          >
            {k === 'all' ? 'All' : KIND[k].label}
          </button>
        ))}
      </fieldset>

      <div className={s.feed}>
        {shown.map((b) => {
          const items = b.sections.flatMap((x) => x.items ?? []);
          return (
            <Link key={b.id} to={`/news/${b.id}`} className={s.card} data-kind={b.kind}>
              <span className={s.cardTop}>
                <span className={s.badge} data-kind={b.kind}>
                  {KIND[b.kind].emoji} {KIND[b.kind].label}
                </span>
                <span className={`${s.when} num`}>{dayLabel(b.date, today, prefs)}</span>
              </span>
              <span className={s.cardTitle}>{b.title}</span>
              {b.headline && <span className={s.headline}>{b.headline}</span>}
              {items.length > 0 && (
                <span className={s.preview}>
                  {items.slice(0, 3).map((it) => (
                    <span key={it.title} className={s.previewLine}>
                      {it.emoji ? `${it.emoji} ` : '· '}
                      {it.title}
                    </span>
                  ))}
                  {items.length > 3 && <span className={s.more}>and {items.length - 3} more</span>}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </Page>
  );
}

const MD_CLASSES = {
  checklist: s.mdList,
  check: undefined,
  done: undefined,
  bullets: s.mdList,
  heading: s.sectionName,
  para: s.mdPara,
};

/** One brief, laid out section by section, every source a link. */
export function BriefScreen({ id }: { id: string }) {
  const brief = useStore((st) => st.tables.briefs.get(id));
  const today = useStore(selectToday);
  const prefs = useStore(selectPrefs);
  if (!brief || brief.deleted)
    return (
      <Page title="News" backTo="/news">
        <EmptyState icon="newspaper" title="This brief isn't here" />
      </Page>
    );
  return (
    <Page title={KIND[brief.kind].label} backTo="/news" wide>
      <header className={s.hero} data-kind={brief.kind}>
        <span className={s.badge} data-kind={brief.kind}>
          {KIND[brief.kind].emoji} {KIND[brief.kind].label} · {dayLabel(brief.date, today, prefs)}
        </span>
        <h1 className={s.heroTitle}>{brief.title}</h1>
        {brief.headline && <p className={s.heroHeadline}>{brief.headline}</p>}
      </header>

      {brief.sections.length === 0 ? (
        <div className={s.markdown}>
          <Markdown text={brief.markdown} classes={MD_CLASSES} />
        </div>
      ) : (
        brief.sections.map((sec) => (
          <section key={sec.name} className={s.section}>
            <h2 className={s.sectionName}>{sec.name}</h2>
            {(sec.items ?? []).map((it) => (
              <article key={it.title} className={s.item}>
                <div className={s.itemHead}>
                  {it.emoji && <span className={s.emoji}>{it.emoji}</span>}
                  <h3 className={s.itemTitle}>
                    {it.url ? (
                      <a href={it.url} target="_blank" rel="noreferrer noopener">
                        {it.title}
                      </a>
                    ) : (
                      it.title
                    )}
                  </h3>
                  {it.cpp && <span className={`${s.cpp} num`}>{it.cpp}</span>}
                </div>
                {it.dates && <p className={`${s.dates} num`}>{it.dates}</p>}
                {(it.minPoints != null || it.maxPoints != null) && (
                  <p className={`${s.limits} num`}>
                    {limitsLine(it.minPoints ?? null, it.maxPoints ?? null, buyCpp(it.cpp))}
                  </p>
                )}
                {it.why && (
                  <p className={s.why}>
                    <Inline text={it.why} />
                  </p>
                )}
                {it.url && <External href={it.url}>{it.source ?? 'Read more'}</External>}
                {!it.url && it.source && <p className={s.dates}>{it.source}</p>}
              </article>
            ))}
            {sec.lines && sec.lines.length > 0 && (
              <ul className={s.lines}>
                {sec.lines.map((l) => (
                  <li key={l}>
                    <Inline text={l} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))
      )}
    </Page>
  );
}
